import { SearchOutlined } from '@ant-design/icons'
import { PageContainer } from '@ant-design/pro-layout'
import { Button, Col, Collapse, Input, Modal, Row, Space, Table, Typography as Text } from 'antd'
import moment from 'moment'
import React, { useRef } from 'react'
import Highlighter from 'react-highlight-words'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'

import Card from '@/components/Card'
import DurationFilterButton from '@/components/DurationFilterButton'
import FilterDialog from '@/components/FilterDialog'
import LoadingIndicator from '@/components/LoadingIndicator'
import ReportActionButtons from '@/components/ReportActionButtons'
import ReportPrintContainer from '@/components/ReportPrintContainer'
import ReportResultCounts from '@/components/ReportResultCounts'
import { pageStyle } from '@/constants'
import dimorderApi from '@/libs/api/dimorderApi'
import { currencyWithCommas } from '@/libs/numberWithCommas'
import { isOneOfRegions, REGIONS } from '@/libs/region'
import { useSelector } from '@/redux'
import colors from '@/style/colors'

export default function PayInAndOutReport(props) {
  const { t } = useTranslation()
  const printRef = useRef()
  const { Panel } = Collapse
  const [params, setParams] = React.useState(null)
  const [payInAndOutReport, setPayInAndOutReport] = React.useState(null)
  const [cashBalanceData, setCashBalanceData] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [activeKey, setActiveKey] = React.useState('1')
  const [reasonSearchText, setReasonSearchText] = React.useState()
  const [tableFilters, setTableFilters] = React.useState({})
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)
  const currentLang = useSelector((state) => state.app.lang)
  const [filteredCount, setFilteredCount] = React.useState(null)

  const currency = activeMerchant?.setting?.currency
  const digits = activeMerchant?.rounding?.minFractionDigits ?? 2
  const isCashBalanceRegion = isOneOfRegions(activeMerchant?.region, [REGIONS.TW, REGIONS.TH])

  React.useEffect(() => {
    if (params) {
      const getItems = async () => {
        setLoading(true)
        try {
          const [payInOutResult, cashBalanceResult] = await Promise.all([
            dimorderApi.instance(activeMerchant.id)?.getPayInAndOutReport(params),
            isCashBalanceRegion
              ? dimorderApi
                  .instance(activeMerchant.id)
                  ?.getCashBalanceReport(params)
                  .catch((err) => {
                    console.warn('getCashBalanceReport failed:', err)
                    return null
                  })
              : Promise.resolve(null),
          ])
          setPayInAndOutReport(payInOutResult)
          setCashBalanceData(cashBalanceResult)
          setFilteredCount(null)
          setActiveKey('0')
        } catch (error) {
          console.log(error)
          Modal.error({
            title: t('app.common.error'),
            content: t('app.common.submitError'),
          })
        }
        setLoading(false)
      }
      getItems()
    }
  }, [params, t, activeMerchant, currentLang])

  let searchInput = null

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {
            searchInput = node
          }}
          placeholder={`${t('app.common.search')}${title}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            {t('app.common.search')}
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, dataIndex)}
            size='small'
            style={{ width: 90 }}
          >
            {t('app.common.reset')}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1890ff' : colors.textSecondary,
          fontSize: 15,
        }}
      />
    ),
    onFilter: (value, record) => {
      if (dataIndex === 'reason') {
        return record[dataIndex]
          ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
          : ''
      }
    },
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.select(), 100)
      }
    },
  })

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    if (dataIndex === 'reason') {
      setReasonSearchText(selectedKeys[0])
    }
  }

  const handleReset = (clearFilters, dataIndex) => {
    clearFilters()
    if (dataIndex === 'reason') {
      setReasonSearchText('')
    }
  }

  const handleTableChange = (pagination, filters, sorter, extra) => {
    setTableFilters(filters)
    setFilteredCount(extra.currentDataSource.length)
  }

  const displayReason = React.useCallback(
    (reason) => {
      const reasons = {
        food: t('page.payInAndOutReport.reasons.food'),
        cleaningSupplies: t('page.payInAndOutReport.reasons.cleaningSupplies'),
        kitchenware: t('page.payInAndOutReport.reasons.kitchenware'),
        tableware: t('page.payInAndOutReport.reasons.tableware'),
        stationery: t('page.payInAndOutReport.reasons.stationery'),
      }

      return reasons[reason] ?? (reason || t('page.payInAndOutReport.reasons.other'))
    },
    [t],
  )

  const displayType = (type) => {
    const types = {
      in: t('page.payInAndOutReport.types.in'),
      out: t('page.payInAndOutReport.types.out'),
    }

    return types[type] ?? type
  }

  // 將 pay-in/out 資料轉為表格列，source === 'orderPayment' 的為現金訂單紀錄
  const mergedTableData = React.useMemo(() => {
    const rows = []

    if (payInAndOutReport) {
      for (const [, value] of Object.entries(payInAndOutReport)) {
        const isOrderPayment = value.source === 'orderPayment'
        const reason = isOrderPayment
          ? value.type === 'in'
            ? `${t('page.payInAndOutReport.cashPayment')} ${value.orderSerial || ''}`.trim()
            : `${t('page.payInAndOutReport.cashRefund')} ${value.orderSerial || ''}`.trim()
          : displayReason(value.reason)
        rows.push({
          key: `payinout-${value.id}`,
          reason,
          type: value.type,
          date: value.createdAt,
          dateText: moment(value.createdAt).format('YYYY-MM-DD HH:mm'),
          amount: value.amount,
        })
      }
    }

    rows.sort((a, b) => new Date(a.date) - new Date(b.date))
    return rows
  }, [payInAndOutReport, t, currentLang, displayReason])

  // 計算摘要數值
  const summary = React.useMemo(() => {
    let totalIn = 0
    let totalOut = 0
    for (const row of mergedTableData) {
      if (row.type === 'in') {
        totalIn += row.amount || 0
      } else if (row.type === 'out') {
        totalOut += row.amount || 0
      }
    }
    return {
      totalIn,
      totalOut,
      net: totalIn - totalOut,
    }
  }, [mergedTableData])

  const detailColumns = [
    {
      title: t('page.payInAndOutReport.reason'),
      dataIndex: 'reason',
      ...getColumnSearchProps('reason', '原因'),
      filteredValue: tableFilters?.reason || null,
      render: (item) => {
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[reasonSearchText]}
            autoEscape
            textToHighlight={item ? item.toString() : ''}
          />
        )
      },
    },
    {
      title: t('page.payInAndOutReport.type'),
      dataIndex: 'type',
      filters: [
        {
          text: t('page.payInAndOutReport.types.in'),
          value: 'in',
        },
        {
          text: t('page.payInAndOutReport.types.out'),
          value: 'out',
        },
      ],
      filteredValue: tableFilters?.type || null,
      onFilter: (value, record) => record?.type === value,
      render: (type) => {
        return <Text>{displayType(type)}</Text>
      },
    },
    {
      title: t('page.payInAndOutReport.date'),
      dataIndex: 'dateText',
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      title: t('page.payInAndOutReport.amount'),
      dataIndex: 'amount',
      sorter: (a, b) => a.amount - b.amount,
      render: (amount) => {
        return (
          <Text>{`${currencyWithCommas(
            amount,
            activeMerchant?.setting?.currency,
            activeMerchant?.rounding?.minFractionDigits,
          )}`}</Text>
        )
      },
    },
  ]

  const cashBalanceColumns = [
    {
      title: t('page.cashBalance.date'),
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
    },
    {
      title: t('page.cashBalance.type'),
      dataIndex: 'typeText',
      key: 'typeText',
      filters: [
        { text: t('page.cashBalance.types.opening'), value: 'opening' },
        { text: t('page.cashBalance.types.closing'), value: 'closing' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: t('page.cashBalance.amount'),
      dataIndex: 'amountText',
      key: 'amountText',
      sorter: (a, b) => a.amount - b.amount,
      align: 'right',
    },
    {
      title: t('page.cashBalance.expectedAmount'),
      dataIndex: 'expectedAmountText',
      key: 'expectedAmountText',
      align: 'right',
    },
    {
      title: t('page.cashBalance.difference'),
      dataIndex: 'differenceText',
      key: 'differenceText',
      align: 'right',
      render: (text, record) => {
        if (record.type !== 'closing') return ''
        const color = record.difference > 0 ? 'green' : record.difference < 0 ? 'red' : undefined
        return <span style={{ color }}>{text}</span>
      },
    },
    {
      title: t('page.cashBalance.cashPayments'),
      dataIndex: 'cashPaymentsText',
      key: 'cashPaymentsText',
      align: 'right',
    },
    {
      title: t('page.cashBalance.cashRefunds'),
      dataIndex: 'cashRefundsText',
      key: 'cashRefundsText',
      align: 'right',
    },
    {
      title: t('page.cashBalance.payIn'),
      dataIndex: 'payInText',
      key: 'payInText',
      align: 'right',
    },
    {
      title: t('page.cashBalance.payOut'),
      dataIndex: 'payOutText',
      key: 'payOutText',
      align: 'right',
    },
    {
      title: t('page.cashBalance.terminalId'),
      dataIndex: 'terminalId',
      key: 'terminalId',
    },
    {
      title: t('page.cashBalance.user'),
      dataIndex: 'user',
      key: 'user',
    },
    {
      title: t('page.cashBalance.remark'),
      dataIndex: 'remark',
      key: 'remark',
    },
  ]

  const cashBalanceTableData = React.useMemo(() => {
    if (!cashBalanceData) return []
    const rows = []
    for (const [, value] of Object.entries(cashBalanceData)) {
      rows.push({
        key: value.id || value.createdAt,
        ...value,
        date: moment(value.createdAt).format('YYYY-MM-DD HH:mm'),
        typeText: t(`page.cashBalance.types.${value.type}`),
        amountText: currencyWithCommas(value.amount, currency, digits),
        expectedAmountText:
          value.type === 'closing'
            ? currencyWithCommas(value.expectedAmount || 0, currency, digits)
            : '',
        differenceText:
          value.type === 'closing'
            ? currencyWithCommas(value.difference || 0, currency, digits)
            : '',
        cashPaymentsText:
          value.type === 'closing'
            ? currencyWithCommas(value.cashPayments || 0, currency, digits)
            : '',
        cashRefundsText:
          value.type === 'closing'
            ? currencyWithCommas(value.cashRefunds || 0, currency, digits)
            : '',
        payInText:
          value.type === 'closing' ? currencyWithCommas(value.payIn || 0, currency, digits) : '',
        payOutText:
          value.type === 'closing' ? currencyWithCommas(value.payOut || 0, currency, digits) : '',
      })
    }
    return rows
  }, [cashBalanceData, currency, digits, t])

  // Section 1: 摘要卡片
  const displaySummaryCard = () => (
    <Row gutter={16} style={{ marginBottom: 24 }}>
      <Col span={24}>
        <Card
          width='100%'
          height='100%'
          padding='20px'
          borderRadius='2px'
          textAlign='left'
          boxShadow='1px 1px  5px rgba(0, 0, 0, 0.3)'
        >
          <Row gutter={32}>
            <Col>
              <div style={{ color: colors.textSecondary, marginBottom: 4 }}>
                {t('page.payInAndOutReport.summary.in')}
              </div>
              <div style={{ fontSize: 20, fontWeight: 600, color: 'green' }}>
                {currencyWithCommas(summary.totalIn, currency, digits)}
              </div>
            </Col>
            <Col>
              <div style={{ color: colors.textSecondary, marginBottom: 4 }}>
                {t('page.payInAndOutReport.summary.out')}
              </div>
              <div style={{ fontSize: 20, fontWeight: 600, color: 'red' }}>
                {currencyWithCommas(summary.totalOut, currency, digits)}
              </div>
            </Col>
            <Col>
              <div style={{ color: colors.textSecondary, marginBottom: 4 }}>
                {t('page.payInAndOutReport.summary.net')}
              </div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>
                {currencyWithCommas(summary.net, currency, digits)}
              </div>
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  )

  // Section 2: 明細表格（pay-in/out + 現金訂單合併）
  const displayDetailTable = () => (
    <Row gutter={16}>
      <Col span={24}>
        <Card
          width='100%'
          height='100%'
          padding='20px'
          borderRadius='2px'
          textAlign='left'
          boxShadow='1px 1px  5px rgba(0, 0, 0, 0.3)'
        >
          <ReportResultCounts counts={filteredCount ?? mergedTableData.length} />
          <br />
          <Table
            columns={detailColumns}
            dataSource={mergedTableData}
            onChange={handleTableChange}
          />
        </Card>
      </Col>
    </Row>
  )

  // Section 3: 現金結餘表格（TW/TH region）
  const displayCashBalance = () => {
    if (!isCashBalanceRegion) return null
    return (
      <Row gutter={16} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card
            width='100%'
            height='100%'
            padding='20px'
            borderRadius='2px'
            textAlign='left'
            boxShadow='1px 1px  5px rgba(0, 0, 0, 0.3)'
          >
            <h3>{t('page.cashBalance.title')}</h3>
            <ReportResultCounts counts={cashBalanceTableData.length} />
            <br />
            <Table columns={cashBalanceColumns} dataSource={cashBalanceTableData} />
          </Card>
        </Col>
      </Row>
    )
  }

  const printResult = () => (
    <ReportPrintContainer
      printRef={printRef}
      start={params?.start}
      end={params?.end}
      title={t('page.payInAndOutReport.title')}
    >
      {/* 摘要 */}
      <Row gutter={32} style={{ marginBottom: 16 }}>
        <Col>
          <span style={{ marginRight: 8 }}>{t('page.payInAndOutReport.summary.in')}:</span>
          <strong>{currencyWithCommas(summary.totalIn, currency, digits)}</strong>
        </Col>
        <Col>
          <span style={{ marginRight: 8 }}>{t('page.payInAndOutReport.summary.out')}:</span>
          <strong>{currencyWithCommas(summary.totalOut, currency, digits)}</strong>
        </Col>
        <Col>
          <span style={{ marginRight: 8 }}>{t('page.payInAndOutReport.summary.net')}:</span>
          <strong>{currencyWithCommas(summary.net, currency, digits)}</strong>
        </Col>
      </Row>
      {/* 明細表格 */}
      <Row gutter={16}>
        <Col span={24}>
          <ReportResultCounts counts={filteredCount ?? mergedTableData.length} />
          <br />
          <Table
            columns={detailColumns}
            dataSource={mergedTableData}
            pagination={false}
            onChange={handleTableChange}
          />
        </Col>
      </Row>
      {/* 現金結餘 */}
      {isCashBalanceRegion && (
        <Row gutter={16} style={{ marginTop: 24 }}>
          <Col span={24}>
            <h3>{t('page.cashBalance.title')}</h3>
            <ReportResultCounts counts={cashBalanceTableData.length} />
            <br />
            <Table
              columns={cashBalanceColumns}
              dataSource={cashBalanceTableData}
              pagination={false}
            />
          </Col>
        </Row>
      )}
    </ReportPrintContainer>
  )

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle,
  })

  const handleExport = async () => {
    try {
      await dimorderApi
        .instance(activeMerchant.id)
        ?.exportPayInAndOutReportCsv(params, isCashBalanceRegion)
    } catch (error) {
      console.log(error)
      Modal.error({
        title: t('app.common.error'),
        content: t('app.common.exportError'),
      })
    }
  }

  return (
    <PageContainer ghost header={{ title: t('page.payInAndOutReport.title') }}>
      <Collapse
        ghost
        expandIcon={() => <></>}
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
      >
        <Panel
          key='1'
          style={{ fontSize: 22 }}
          extra={
            <DurationFilterButton start={params?.start} end={params?.end} activeKey={activeKey} />
          }
        >
          <FilterDialog momentFormatX setParams={setParams} />
        </Panel>
      </Collapse>
      <br />
      <LoadingIndicator loading={loading} />
      {payInAndOutReport && !loading && (
        <>
          <ReportActionButtons onPrint={handlePrint} onExport={handleExport} />
          {displaySummaryCard()}
          {displayDetailTable()}
          {displayCashBalance()}
          {printResult()}
        </>
      )}
    </PageContainer>
  )
}
